import { useCallback, useEffect, useState } from "react";

import { cloudService } from "../cloudService";

import type { CloudFolder } from "../types";

// Returns the immediate child folder names directly under `current`.
const directChildFolders = (
  folders: CloudFolder[],
  current: string,
): string[] => {
  const prefix = current ? `${current}/` : "";
  const names = new Set<string>();
  for (const f of folders) {
    if (current === "" ? !f.path.includes("/") : f.path.startsWith(prefix)) {
      const rest = f.path.slice(prefix.length);
      const name = rest.split("/")[0];
      if (name) {
        names.add(name);
      }
    }
  }
  return [...names].sort();
};

/**
 * Folder browser used by the Save-to-cloud dialog. The currently-navigated
 * path IS the destination folder (shown in the breadcrumb). Users can drill
 * into subfolders or create a new folder inline.
 */
export const FolderPicker = ({
  path,
  onPathChange,
}: {
  path: string;
  onPathChange: (path: string) => void;
}) => {
  const [folders, setFolders] = useState<CloudFolder[]>([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setFolders(await cloudService.listFolders());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const subfolders = directChildFolders(folders, path);
  const breadcrumbs = path ? path.split("/") : [];

  const createFolder = async () => {
    const name = window.prompt("New folder name");
    if (!name || !name.trim()) {
      return;
    }
    const newPath = path ? `${path}/${name.trim()}` : name.trim();
    await cloudService.createFolder(newPath);
    await refresh();
    onPathChange(newPath);
  };

  return (
    <div
      style={{
        border: "1px solid var(--color-gray-30)",
        borderRadius: "0.5rem",
        padding: "0.5rem",
        display: "flex",
        flexDirection: "column",
        gap: "0.5rem",
      }}
    >
      {/* Breadcrumb = chosen destination */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.25rem",
          flexWrap: "wrap",
        }}
      >
        <span style={{ fontSize: "0.8rem", color: "var(--color-gray-60)" }}>
          Save in:
        </span>
        <button type="button" style={linkBtn} onClick={() => onPathChange("")}>
          Home
        </button>
        {breadcrumbs.map((seg, i) => {
          const p = breadcrumbs.slice(0, i + 1).join("/");
          return (
            <span key={p}>
              {" / "}
              <button
                type="button"
                style={linkBtn}
                onClick={() => onPathChange(p)}
              >
                {seg}
              </button>
            </span>
          );
        })}
      </div>

      {/* Subfolder list */}
      <div
        style={{
          maxHeight: 160,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: "0.15rem",
        }}
      >
        {loading ? (
          <div style={{ fontSize: "0.8rem", padding: "0.5rem" }}>Loading…</div>
        ) : subfolders.length ? (
          subfolders.map((name) => (
            <button
              key={name}
              type="button"
              style={folderRow}
              onClick={() => onPathChange(path ? `${path}/${name}` : name)}
            >
              📁 {name}
            </button>
          ))
        ) : (
          <div
            style={{
              fontSize: "0.8rem",
              color: "var(--color-gray-60)",
              padding: "0.25rem",
            }}
          >
            No subfolders here.
          </div>
        )}
      </div>

      <button
        type="button"
        style={{ ...linkBtn, alignSelf: "flex-start" }}
        onClick={createFolder}
      >
        + New folder
      </button>
    </div>
  );
};

const linkBtn: React.CSSProperties = {
  background: "none",
  border: "none",
  color: "var(--color-primary)",
  cursor: "pointer",
  padding: 0,
  font: "inherit",
};

const folderRow: React.CSSProperties = {
  background: "none",
  border: "none",
  textAlign: "left",
  cursor: "pointer",
  padding: "0.3rem 0.4rem",
  borderRadius: "0.35rem",
  font: "inherit",
};
